import { closeIconV2 } from '@/shared/ui/assets';
import { CustomButton } from '@/shared/ui/components';
import { Modal } from 'antd';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useLearnMoreUniversalCiedManagementModal } from './useLearnMoreUniversalCiedManagementModal';
import BiocareLogo from './assets/biocare-logo.svg';
import type { LearnMoreUniversalCiedManagementModalProps } from './learnMoreUniversalCiedManagementModal.types';
import './learnMoreUniversalCiedManagementModal.scss';

// WCAG "visually hidden" pattern: kept in the accessibility tree (unlike display:none) so the
// dialog's aria-labelledby (wired by antd's `title` prop) resolves to a real accessible name,
// without adding a visible heading this marketing modal's own layout doesn't have room for.
const VISUALLY_HIDDEN_STYLE = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

const LearnMoreUniversalCiedManagementModal = ({
  open,
  handleClickCloseButton,
}: LearnMoreUniversalCiedManagementModalProps) => {
  const { state, onClickLearnMore } = useLearnMoreUniversalCiedManagementModal({
    handleClickCloseButton,
  });

  return (
    <Modal
      open={open}
      destroyOnClose
      width={800}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      className="learn-more-universal-cied-management-modal"
      title={<span style={VISUALLY_HIDDEN_STYLE}>{t`Universal CIED management`}</span>}
      styles={{ header: VISUALLY_HIDDEN_STYLE }}
    >
      <CustomButton
        id="close-btn"
        className="close-btn"
        style="neutral"
        type="tertiary"
        icon={<ReactSVG src={closeIconV2} />}
        onClick={handleClickCloseButton}
        ariaLabel={t`Close`}
      />
      <div className="logo-container">
        <img src={BiocareLogo} alt="Biocare logo" />
      </div>
      <div className="content-container">
        <div className="content">
          <div className="title">{t`COMPREHENSIVE CARE & UNIVERSAL INTEGRATION`}</div>
          <p>
            Our platform offers seamless integration with all your implantable device manufacturers,
            providing you with instant access to critical patient data for both remote monitoring
            and in-clinic visits. This universal compatibility ensures that every patient,
            regardless of device type or manufacturer, receives the highest standard of care.
          </p>
        </div>
        <div className="content">
          <div className="title">{t`OPERATIONAL EXCELLENCE & STREAMLINED WORKFLOWS`}</div>
          <p>
            Designed for maximum efficiency, our platform revolutionizes clinic workflows by
            offering a centralized, intuitive interface for managing patient data. This streamlines
            the care process, reduces administrative burden, and allows you to focus on patient care
            instead of navigating multiple portals.
          </p>
        </div>
        <div className="title-divider-container">
          <span className="title">FUTURE RELEASE</span>
          <span className="divider" />
        </div>
        <div className="content">
          <div className="title">{t`INSIGHTFUL ANALYTICS & ADVANCED AUTOMATION`}</div>
          <p>
            Leveraging data analytics, our platform provides actionable insights into patient health
            trends and device performance allowing you to make informed decisions and improve
            patient outcomes.
          </p>
        </div>
        <div className="content">
          <div className="title">{t`AUTOMATED BILLING & CLAIMS MANAGEMENT`}</div>
          <p>
            Our advanced automation capabilities streamline the billing and claims process, ensuring
            accuracy and efficiency in claims management and compliance.
          </p>
        </div>
      </div>
      <div className="button-container">
        <CustomButton
          size="middle"
          type="primary"
          onClick={onClickLearnMore}
          className="learn-more-btn"
          loading={state.isLoading}
        >
          {t`CLICK HERE TO LEARN MORE`}
        </CustomButton>
      </div>
    </Modal>
  );
};

export default LearnMoreUniversalCiedManagementModal;
